import React, { useCallback, useEffect, useRef, useState } from 'react';
import { HeaderBar } from './HeaderBar';
import { CanvasContainer } from './CanvasContainer';
import { BackgroundControls } from './BackgroundControls';
import { ContextMenu } from './ContextMenu';
import { InputModal } from './Modal';
import { WireEditModal } from './WireEditModal';
import { SectionPropertiesModal } from './SectionPropertiesModal';
import { WirePanel } from './WirePanel';
import { ConnectorTypePanel } from './ConnectorTypePanel';
import { ConnectorTypeForm } from './ConnectorTypeForm';
import { usePixiApp } from '../hooks/usePixiApp';
import { useStorage } from '../hooks/useStorage';
import { useHarnessInteractions } from '../hooks/useHarnessInteractions';
import { StateManager } from '../managers/StateManager';
import { printReport, buildCutListCsv, buildBomCsv, downloadTextFile } from '../export';
import { COLORS } from '../utils/constants';
import { logger } from '../utils/logger';

export const KiwiHarness: React.FC = () => {
  const stateManagerRef = useRef(new StateManager());

  const {
    contextMenu,
    hideContextMenu,
    inputModal,
    hideInputModal,
    wireEditId,
    hideWireEdit,
    sectionEdit,
    hideSectionEdit,
    handleAddConnectorFromType,
    contextMenuHandlers
  } = useHarnessInteractions(stateManagerRef.current);

  const { initializeCanvas, fitToView, screenToWorld, cleanup } = usePixiApp(stateManagerRef.current, contextMenuHandlers);
  const { loadSavedData, exportData, importData, clearAllData } = useStorage(stateManagerRef.current);
  const [isInitializing, setIsInitializing] = useState(false);
  const [initError, setInitError] = useState<string | null>(null);
  const [editingTypeId, setEditingTypeId] = useState<string | null>(null);
  const [creatingType, setCreatingType] = useState(false);
  const [typesCollapsed, setTypesCollapsed] = useState(false);
  const [typesWidth, setTypesWidth] = useState(260);
  const [wiresCollapsed, setWiresCollapsed] = useState(false);
  const [wiresWidth, setWiresWidth] = useState(320);
  const [showBackground, setShowBackground] = useState(false);
  const [history, setHistory] = useState({ canUndo: false, canRedo: false });

  const stateManager = stateManagerRef.current;

  // Keep the undo/redo controls in sync with the history stacks.
  useEffect(() => {
    setHistory(stateManager.getHistoryState());
    return stateManager.subscribeToHistory(setHistory);
  }, [stateManager]);

  // Global undo/redo shortcuts, ignored while typing in a text field.
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!(event.ctrlKey || event.metaKey) || event.key.toLowerCase() !== 'z') {
        return;
      }
      const target = event.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) {
        return;
      }
      event.preventDefault();
      if (event.shiftKey) {
        stateManager.redo();
      } else {
        stateManager.undo();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [stateManager]);

  const handleCanvasReady = useCallback(async (container: HTMLDivElement) => {
    try {
      setIsInitializing(true);
      setInitError(null);

      // Load saved data BEFORE canvas initialization
      loadSavedData();

      await initializeCanvas(container);

      // Frame the whole graph once the entities are on the canvas.
      fitToView();

      setIsInitializing(false);
    } catch (error) {
      logger.pixi.error('Canvas initialization failed', error);
      setInitError(error instanceof Error ? error.message : 'Unknown initialization error');
      setIsInitializing(false);
    }

    return () => {
      cleanup();
    };
  }, [initializeCanvas, fitToView, cleanup, loadSavedData]);

  const handleTypeDrop = useCallback((typeId: string, clientX: number, clientY: number) => {
    const world = screenToWorld(clientX, clientY);
    if (world) {
      handleAddConnectorFromType(typeId, world.x, world.y);
    }
  }, [screenToWorld, handleAddConnectorFromType]);

  const handleImport = useCallback(() => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        importData(file);
      }
    };
    input.click();
  }, [importData]);

  const handlePrint = useCallback(() => {
    const opened = printReport(stateManager.getState());
    if (!opened) {
      alert('Could not open the report window. Please allow pop-ups for this site and try again.');
    }
  }, [stateManager]);

  const handleExportCutListCsv = useCallback(() => {
    const date = new Date().toISOString().split('T')[0];
    downloadTextFile(`kiwi-cutlist-${date}.csv`, buildCutListCsv(stateManager.getState()), 'text/csv');
  }, [stateManager]);

  const handleExportBomCsv = useCallback(() => {
    const date = new Date().toISOString().split('T')[0];
    downloadTextFile(`kiwi-bom-${date}.csv`, buildBomCsv(stateManager.getState()), 'text/csv');
  }, [stateManager]);

  // Suppress the native browser context menu across the app (canvas, custom
  // menus and overlays) so it never appears alongside our own menu. Text inputs
  // keep their native menu so right-click paste still works in modals.
  const suppressNativeContextMenu = useCallback((event: React.MouseEvent) => {
    const target = event.target as HTMLElement;
    const isTextField = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable;
    if (!isTextField) {
      event.preventDefault();
    }
  }, []);

  return (
    <div
      onContextMenu={suppressNativeContextMenu}
      style={{
        width: '100vw',
        height: '100vh',
        margin: 0,
        padding: 0,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
      }}
    >
      <HeaderBar
        onPrint={handlePrint}
        onExportCutListCsv={handleExportCutListCsv}
        onExportBomCsv={handleExportBomCsv}
        onExportJson={exportData}
        onImport={handleImport}
        onClearAll={clearAllData}
        onToggleBackground={() => setShowBackground(v => !v)}
        onUndo={() => stateManager.undo()}
        onRedo={() => stateManager.redo()}
        canUndo={history.canUndo}
        canRedo={history.canRedo}
      />

      {initError && (
        <div style={{
          position: 'absolute',
          top: '70px',
          left: '20px',
          right: '20px',
          backgroundColor: COLORS.DANGER,
          color: 'white',
          padding: '10px',
          borderRadius: '4px',
          zIndex: 1000
        }}>
          <strong>Initialization Error:</strong> {initError}
          <button
            onClick={() => setInitError(null)}
            style={{
              marginLeft: '10px',
              backgroundColor: 'transparent',
              border: '1px solid white',
              color: 'white',
              padding: '2px 8px',
              borderRadius: '2px',
              cursor: 'pointer'
            }}
          >
            Dismiss
          </button>
        </div>
      )}

      {isInitializing && (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          backgroundColor: 'rgba(0,0,0,0.8)',
          color: 'white',
          padding: '20px',
          borderRadius: '8px',
          zIndex: 1000
        }}>
          Initializing Kiwi Harness...
        </div>
      )}

      <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
        <ConnectorTypePanel
          stateManager={stateManager}
          onEditType={setEditingTypeId}
          onCreateType={() => setCreatingType(true)}
          collapsed={typesCollapsed}
          onToggleCollapsed={() => setTypesCollapsed(c => !c)}
          width={typesWidth}
          onWidthChange={setTypesWidth}
        />

        <div style={{ flex: 1, minWidth: 0, position: 'relative', display: 'flex' }}>
          <CanvasContainer onCanvasReady={handleCanvasReady} onConnectorTypeDrop={handleTypeDrop} />

          <BackgroundControls
            stateManager={stateManager}
            screenToWorld={screenToWorld}
            visible={showBackground}
            onClose={() => setShowBackground(false)}
          />

          <button
            onClick={fitToView}
            title="Fit all elements into view"
            style={{
              position: 'absolute',
              bottom: '16px',
              left: '50%',
              transform: 'translateX(-50%)',
              padding: '8px 16px',
              border: '1px solid #dfe4ea',
              borderRadius: '20px',
              backgroundColor: 'white',
              color: '#2d3436',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              cursor: 'pointer',
              fontSize: '13px',
              zIndex: 400
            }}
          >
            Fit to view
          </button>
        </div>

        <WirePanel
          stateManager={stateManager}
          contextMenuHandlers={contextMenuHandlers}
          collapsed={wiresCollapsed}
          onToggleCollapsed={() => setWiresCollapsed(c => !c)}
          width={wiresWidth}
          onWidthChange={setWiresWidth}
        />
      </div>

      {(editingTypeId || creatingType) && (
        <ConnectorTypeForm
          stateManager={stateManager}
          typeId={editingTypeId ?? undefined}
          onClose={() => { setEditingTypeId(null); setCreatingType(false); }}
        />
      )}

      {/* Context Menu */}
      {contextMenu && (
        <ContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          items={contextMenu.items}
          onClose={hideContextMenu}
        />
      )}

      {wireEditId && (
        <WireEditModal
          stateManager={stateManager}
          wireId={wireEditId}
          onClose={hideWireEdit}
        />
      )}

      {sectionEdit && (
        <SectionPropertiesModal
          stateManager={stateManager}
          aId={sectionEdit.aId}
          bId={sectionEdit.bId}
          onClose={hideSectionEdit}
        />
      )}

      {/* Input Modal */}
      {inputModal && (
        <InputModal
          title={inputModal.title}
          label={inputModal.label}
          initialValue={inputModal.initialValue}
          onConfirm={inputModal.onConfirm}
          onCancel={hideInputModal}
        />
      )}
    </div>
  );
};

export default KiwiHarness;
